import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeftRight, ChevronDown, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useTacticLink } from "@/hooks/useTacticLink";
import { tacticOrigin } from "@/lib/tacticOrigin";
import { toast } from "@/hooks/use-toast";
import { useCanSwitchApps } from "@/hooks/useCanSwitchApps";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

/**
 * «Ulture Pest · empresa» arriba de la barra lateral (ordenador): con Ulture
 * Tactic conectada, al pulsarlo sale «Cambiar a Ulture Tactic» y se abre Tactic
 * con la sesión iniciada (token de un solo uso). Sin conexión, como siempre.
 * Solo en la web de ordenador (04-11-2026): en el móvil y en la app nativa, no.
 */
export function AppSwitcherTitle({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { company } = useAuth();
  const { data } = useTacticLink(company?.id);
  const canSwitch = useCanSwitchApps();
  const [going, setGoing] = useState(false);
  if (!data?.linked || !canSwitch) return <>{children}</>;

  const go = async () => {
    setGoing(true);
    try {
      const { data: r, error } = await supabase.functions.invoke("app-switch", { body: { action: "create", target_origin: tacticOrigin() } });
      if (error || typeof r?.url !== "string") throw error ?? new Error("sin url");
      window.location.href = r.url;
    } catch (e) {
      console.error("[AppSwitcherTitle]", e);
      toast({ title: t("tacticLink.errors.generic"), variant: "destructive" });
      setGoing(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div role="button" tabIndex={0} className="group cursor-pointer rounded-lg -m-1 p-1 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60" data-testid="app-switcher">
          <div className="flex items-start gap-1">
            <div className="min-w-0 flex-1">{children}</div>
            <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-white/80 group-hover:text-white" aria-hidden />
          </div>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); void go(); }} className="gap-2 font-semibold">
          {going ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowLeftRight className="h-4 w-4" />}
          <span className="min-w-0">
            {t("tacticLink.switchTo")}
            {data.tactic_company_name && <span className="block text-xs font-normal text-muted-foreground truncate">{data.tactic_company_name}</span>}
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
